import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../modal-escape-guard/modal-escape-guard.directive';
import { FileDropzoneComponent } from '../file-dropzone/file-dropzone.component';
import { ProjectDocumentService } from '../../services/project-document.service';
import { DocumentTargetType, PROJECT_DOCUMENT_MAX_BYTES, ProjectDocument } from '../../models/project-document.model';
import { saveBlob } from '../../shared/file-download';

/**
 * Karte „Dokumente“ in der rechten Spalte von Testfall, Modul und Anforderung (NEX-2268).
 *
 * Ein Dokument liegt einmal im Projekt; die Karte zeigt die Verknuepfungen dieses Ziels.
 * Hochladen verknuepft gleich mit, Loesen laesst das Dokument im Projekt.
 */
@Component({
  selector: 'app-project-documents-card',
  imports: [TranslatePipe, MatIcon, MatProgressSpinner, Modal, ModalEscapeGuardDirective, FileDropzoneComponent],
  templateUrl: './project-documents-card.component.html',
  styleUrl: './project-documents-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectDocumentsCardComponent implements AfterViewInit {
  private readonly service = inject(ProjectDocumentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly projectId = input.required<string>();
  readonly targetType = input.required<DocumentTargetType>();
  readonly targetId = input.required<string>();
  readonly canWrite = input(false);

  readonly maxMegabytes = PROJECT_DOCUMENT_MAX_BYTES / (1024 * 1024);

  readonly documents = signal<ProjectDocument[]>([]);
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly busy = signal(false);
  /** Uebersetzungsschluessel der letzten gescheiterten Aktion. */
  readonly errorKey = signal<string | null>(null);
  /** Der Dokumentname zur Meldung „Name schon vergeben“. */
  readonly errorName = signal('');
  readonly uploadOpen = signal(false);
  readonly copiedId = signal<string | null>(null);

  readonly linkDialogVisible = signal(false);
  readonly projectDocuments = signal<ProjectDocument[] | null>(null);
  readonly search = signal('');
  readonly selectedId = signal<string | null>(null);

  readonly dialogDocuments = computed(() => {
    const term = this.search().trim().toLowerCase();
    const all = this.projectDocuments() ?? [];
    if (!term) return all;
    return all.filter(
      (d) => d.name.toLowerCase().includes(term) || (d.originalFilename ?? '').toLowerCase().includes(term),
    );
  });

  @ViewChild('linkDialog') linkDialogTemplate!: TemplateRef<unknown>;
  resolvedTemplate: TemplateRef<unknown> | null = null;

  constructor() {
    effect(() => {
      const projectId = this.projectId();
      const targetType = this.targetType();
      const targetId = this.targetId();
      untracked(() => this.load(projectId, targetType, targetId));
    });
  }

  ngAfterViewInit(): void {
    this.resolvedTemplate = this.linkDialogTemplate;
    this.cdr.detectChanges();
  }

  onFileSelected(file: File): void {
    this.errorKey.set(null);
    this.errorName.set(file.name);
    if (file.size > PROJECT_DOCUMENT_MAX_BYTES) {
      this.errorKey.set('documents.errors.tooLarge');
      return;
    }
    this.run(
      this.service
        .upload(this.projectId(), file)
        .pipe(
          switchMap((created) => this.service.link(this.projectId(), this.targetType(), this.targetId(), created.id)),
        ),
      (linked) => {
        this.addRow(linked);
        this.uploadOpen.set(false);
      },
    );
  }

  download(document: ProjectDocument): void {
    this.errorKey.set(null);
    this.run(this.service.download(this.projectId(), document.id), (blob) =>
      saveBlob(blob, document.originalFilename ?? document.name),
    );
  }

  unlink(document: ProjectDocument): void {
    this.errorKey.set(null);
    this.run(this.service.unlink(this.projectId(), this.targetType(), this.targetId(), document.id), () =>
      this.documents.update((list) => list.filter((d) => d.id !== document.id)),
    );
  }

  copyPath(document: ProjectDocument): void {
    if (!document.externalRef) return;
    navigator.clipboard?.writeText(document.externalRef).then(
      () => this.copiedId.set(document.id),
      () => this.errorKey.set('documents.errors.copy'),
    );
  }

  openLinkDialog(): void {
    this.errorKey.set(null);
    this.search.set('');
    this.selectedId.set(null);
    this.projectDocuments.set(null);
    this.linkDialogVisible.set(true);
    this.service
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // Der Server liefert neueste zuerst (NEX-2351), der Dialog bleibt alphabetisch.
        next: (all) => this.projectDocuments.set([...all].sort((a, b) => a.name.localeCompare(b.name))),
        error: () => {
          this.projectDocuments.set([]);
          this.errorKey.set('documents.errors.load');
        },
      });
  }

  isLinked(documentId: string): boolean {
    return this.documents().some((d) => d.id === documentId);
  }

  select(documentId: string): void {
    if (this.isLinked(documentId)) return;
    this.selectedId.set(documentId);
  }

  confirmLink(): void {
    const documentId = this.selectedId();
    if (!documentId) return;
    this.run(this.service.link(this.projectId(), this.targetType(), this.targetId(), documentId), (linked) => {
      this.addRow(linked);
      this.linkDialogVisible.set(false);
    });
  }

  closeLinkDialog(): void {
    this.linkDialogVisible.set(false);
  }

  isReference(document: ProjectDocument): boolean {
    return document.externalRef !== null;
  }

  iconFor(document: ProjectDocument): string {
    if (this.isReference(document)) return 'link';
    const name = (document.originalFilename ?? document.name).toLowerCase();
    const extension = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
    if (['doc', 'docx', 'docm', 'odt', 'rtf'].includes(extension)) return 'description';
    if (['xls', 'xlsx', 'xlsm', 'ods', 'csv'].includes(extension)) return 'table_chart';
    if (extension === 'pdf') return 'picture_as_pdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) return 'image';
    if (['txt', 'log', 'md', 'xml', 'json'].includes(extension)) return 'article';
    return 'insert_drive_file';
  }

  formatSize(bytes: number | null): string {
    if (bytes === null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private load(projectId: string, targetType: DocumentTargetType, targetId: string): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.service
      .listLinked(projectId, targetType, targetId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list) => {
          this.documents.set(list);
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private addRow(document: ProjectDocument): void {
    this.documents.update((list) =>
      [...list.filter((d) => d.id !== document.id), document].sort((a, b) => a.name.localeCompare(b.name)),
    );
  }

  private run<T>(call: Observable<T>, onSuccess: (value: T) => void): void {
    this.busy.set(true);
    call.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (value) => {
        this.busy.set(false);
        onSuccess(value);
      },
      error: (error: unknown) => {
        this.busy.set(false);
        this.errorKey.set(errorKeyFor(error));
      },
    });
  }
}

function errorKeyFor(error: unknown): string {
  const status = error instanceof HttpErrorResponse ? error.status : 0;
  if (status === 413) return 'documents.errors.tooLarge';
  if (status === 409) return 'documents.errors.duplicate';
  if (status === 403) return 'documents.errors.forbidden';
  return 'documents.errors.generic';
}
